import React, {useEffect, useRef, useState} from 'react';
import {Icon} from '../../../uiToolkit/Icon/Icon';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {
  POST_IMAGE_DURATION_SECONDS,
  PostMediaItem,
} from '../../../data/models/Posts/Post';
import {postsText} from '../postsText';
import styles from './PostPhoneScreen.module.css';

interface Props {
  items: PostMediaItem[];
  title: string;
  caption?: string;
  index: number;
  onIndexChange: (index: number) => void;
  isLoading?: boolean;
  onProgress?: (fraction: number) => void;
}

const PROGRESS_TICK_MS = 100;

const IMAGE_DURATION_MS = POST_IMAGE_DURATION_SECONDS * 1000;

const PauseIcon = ({paused}: {paused: boolean}) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    {paused ? (
      <path d="M7 4l13 8-13 8V4z" fill="currentColor" />
    ) : (
      <path
        d="M8 5v14M16 5v14"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    )}
  </svg>
);

const MutedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FitIcon = ({filled}: {filled: boolean}) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d={
        filled
          ? 'M10 14l-6 6m0 0h5m-5 0v-5M14 10l6-6m0 0h-5m5 0v5'
          : 'M4 20l6-6m-6 6h5m-5 0v-5M20 4l-6 6m6-6h-5m5 0v5'
      }
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PostPhoneScreen = React.memo(
  ({
    items,
    title,
    caption,
    index,
    onIndexChange,
    isLoading,
    onProgress,
  }: Props) => {
    const [fillFrame, setFillFrame] = useState(false);
    // Autoplay is only allowed while muted; unmuting needs a user gesture,
    // which is what the "Tap for sound" pill provides.
    const [isMuted, setIsMuted] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [progressPaused, setProgressPaused] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    const current = items[index];
    const lastIndex = items.length - 1;
    const timerRuns = !!current && !current.isVideo && index < lastIndex;

    useEffect(() => {
      setElapsed(0);
    }, [index]);

    useEffect(() => {
      if (!timerRuns || progressPaused) return;
      const tick = window.setInterval(
        () => setElapsed(value => value + PROGRESS_TICK_MS),
        PROGRESS_TICK_MS,
      );
      return () => window.clearInterval(tick);
    }, [timerRuns, progressPaused, index]);

    useEffect(() => {
      if (timerRuns && elapsed >= IMAGE_DURATION_MS) {
        onIndexChange(Math.min(lastIndex, index + 1));
      }
    }, [elapsed, timerRuns, lastIndex, index, onIndexChange]);

    useEffect(() => {
      if (!onProgress || !current || current.isVideo) return;
      onProgress(Math.min(elapsed / IMAGE_DURATION_MS, 1));
    }, [elapsed, current, onProgress]);

    const onUnmute = () => {
      const video = videoRef.current;
      if (video) {
        video.muted = false;
      }
    };

    const togglePlayback = () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    };

    const onVideoTimeUpdate = () => {
      const video = videoRef.current;
      if (!onProgress || !video || !video.duration) return;
      onProgress(Math.min(video.currentTime / video.duration, 1));
    };

    const mediaClass = `${styles.media} ${
      fillFrame ? styles.mediaCover : styles.mediaContain
    }`;

    return (
      <div className={styles.screen}>
        <span className={styles.positionChip}>
          {items.length > 1 ? `${title} · ${index + 1}/${items.length}` : title}
        </span>
        {timerRuns && (
          <span className={styles.progressTrack}>
            <span
              className={styles.progressFill}
              style={{
                width: `${Math.min((elapsed / IMAGE_DURATION_MS) * 100, 100)}%`,
              }}
            />
          </span>
        )}
        <div className={styles.stage}>
          {isLoading && <LoadingSpinner size={36} />}
          {!isLoading &&
            current &&
            (current.isVideo ? (
              <video
                key={current.id}
                ref={videoRef}
                className={mediaClass}
                src={current.url}
                poster={current.poster}
                autoPlay
                muted
                playsInline
                onClick={togglePlayback}
                onPlay={() => setIsPaused(false)}
                onPause={() => setIsPaused(true)}
                onVolumeChange={ev => setIsMuted(ev.currentTarget.muted)}
                onTimeUpdate={onVideoTimeUpdate}
                onEnded={() => onIndexChange(Math.min(lastIndex, index + 1))}
              />
            ) : (
              <img className={mediaClass} src={current.url} alt="" />
            ))}
          {current?.isVideo && isPaused && (
            <span className={styles.pausedBadge}>
              <Icon name="play_ic.svg" className={styles.pausedIcon} />
            </span>
          )}
          {current?.isVideo && isMuted && (
            <button
              className={styles.unmuteButton}
              onClick={onUnmute}
              aria-label={postsText.unmute}>
              <MutedIcon />
              <span>{postsText.unmute}</span>
            </button>
          )}
          {timerRuns && (
            <button
              type="button"
              className={styles.progressButton}
              onClick={() => setProgressPaused(!progressPaused)}
              title={
                progressPaused
                  ? postsText.resumeProgress
                  : postsText.pauseProgress
              }
              aria-label={
                progressPaused
                  ? postsText.resumeProgress
                  : postsText.pauseProgress
              }>
              <PauseIcon paused={progressPaused} />
            </button>
          )}
          {!!current && (
            <button
              type="button"
              className={styles.fitButton}
              onClick={() => setFillFrame(!fillFrame)}
              title={fillFrame ? postsText.fitToFrame : postsText.fillFrame}
              aria-label={
                fillFrame ? postsText.fitToFrame : postsText.fillFrame
              }>
              <FitIcon filled={fillFrame} />
            </button>
          )}
        </div>
        {caption ? <p className={styles.caption}>{caption}</p> : null}
      </div>
    );
  },
);

export default PostPhoneScreen;
